import * as Yup from 'yup';
import { Col, Row, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { FileTextOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload/interface';
import FileActions from 'components/new-ui/base/file-actions/FileActions';
import FileIcon from 'components/new-ui/base/file-icon/FileIcon';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormDatePicker from 'components/new-ui/composed/formDatePicker/FormDatePicker';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import UploadDocuments from 'components/new-ui/composed/upload-documents/UploadDocuments';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE, S3KEY_REGEX } from 'core/utils/constants';
import { createCustomerFile, getDocumentUploadUrl } from 'store/customers/client';
import { getErrorMessage } from 'utils/getErrorMessage';
import { uploadFile } from 'utils/uploadFile';
import { validateCustomerFile } from 'utils/validateCustomerFile';

const validationSchema = Yup.object({
    file: Yup.mixed<RcFile>().required('Please select a file to upload.'),
    effectiveSince: Yup.string().nullable().required('AEP5 Effective From is required.'),
    effectiveTo: Yup.string()
        .nullable()
        .test(
            'is-after-effective-since',
            'AEP5 Effective To must be on or after the Effective From date.',
            function (value) {
                const { effectiveSince } = this.parent;
                if (!value || !effectiveSince) return true;
                return new Date(value) >= new Date(effectiveSince);
            }
        ),
});

type Props = {
    open: boolean;
    customerId: string;
    onClose: () => void;
    onUploaded?: () => void;
};

const AEP5DocumentUploadModal = ({ open, customerId, onClose, onUploaded }: Props) => {
    const [isUploading, setIsUploading] = useState(false);

    const formik = useFormik<{ file: RcFile | null; effectiveSince: string | null; effectiveTo: string | null }>({
        initialValues: {
            file: null,
            effectiveSince: null,
            effectiveTo: null,
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: () => undefined,
    });

    const { values, isValid, setFieldValue, resetForm } = formik;

    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, resetForm]);

    const handleUpload = async () => {
        const { file, effectiveSince, effectiveTo } = values;

        if (!isValid || !file || !effectiveSince) return;

        setIsUploading(true);
        try {
            const { preSignedUrl } = await getDocumentUploadUrl(customerId, file.name, file.size);
            await uploadFile(file, preSignedUrl);

            const match = preSignedUrl.match(S3KEY_REGEX);
            const s3key = match?.[1];

            if (!s3key) {
                throw new Error('Could not extract s3key from presigned url');
            }

            await createCustomerFile(customerId, {
                s3key,
                fileName: file.name,
                fileSize: file.size,
                activeFrom: effectiveSince,
                activeTo: effectiveTo || undefined,
                // The AEP5 document is the proof of the party's customs authorisation
                customsVerificationDocument: true,
            });

            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'AEP5 document uploaded',
                description: `${file.name} was uploaded successfully.`,
            });

            onUploaded?.();
            onClose();
        } catch (err) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Upload failed',
                description: getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <ConfirmationModal
            icon={<FileTextOutlined />}
            title="AEP5 Document Upload"
            cancelButtonText="I'll Upload Later"
            confirmButtonText="Upload"
            open={open}
            onCancel={onClose}
            onConfirm={handleUpload}
            confirmationButtonDisabled={!isValid}
            confirmationButtonLoading={isUploading}
            content={
                <FormikProvider value={formik}>
                    <Space size={32} direction="vertical" style={{ width: '100%', paddingTop: 8, paddingBottom: 16 }}>
                        <div style={{ width: '100%' }}>
                            <UploadDocuments
                                key={open ? 'open' : 'closed'}
                                showUploadList={false}
                                maxCount={1}
                                beforeUpload={validateCustomerFile}
                                customRequest={(options) => {
                                    setFieldValue('file', options.file as RcFile);
                                    options.onSuccess?.({});
                                }}
                            />

                            {values.file && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginTop: 16,
                                    }}
                                >
                                    <Space size={8}>
                                        <FileIcon type={values.file.name.split('.').pop()?.toLowerCase()} />
                                        <Typography.Text>{values.file.name}</Typography.Text>
                                    </Space>
                                    <FileActions
                                        delete={{
                                            tooltip: 'Remove file',
                                            onClick: () => setFieldValue('file', null),
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <Row>
                            <Col span={12} style={{ paddingRight: 12 }}>
                                <Col>
                                    <Space size={8}>
                                        <LabelRequiredSymbol />
                                        <label htmlFor="effectiveSince"> AEP5 Effective From</label>
                                    </Space>
                                </Col>
                                <Col>
                                    <FormDatePicker fieldName="effectiveSince" />
                                </Col>
                            </Col>
                            <Col span={12} style={{ paddingLeft: 12 }}>
                                <Col>
                                    <label htmlFor="effectiveTo"> AEP5 Effective To</label>
                                </Col>
                                <Col>
                                    <FormDatePicker fieldName="effectiveTo" />
                                </Col>
                            </Col>
                        </Row>
                    </Space>
                </FormikProvider>
            }
        />
    );
};

export default AEP5DocumentUploadModal;
